import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactGA from 'react-ga4';

// Styled Components
const LandingContainer = styled.div`
  font-family: 'Poppins', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background: linear-gradient(to right, #2D3142 70%, #E94057 100%);
  color: white;
  padding: 6rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  max-width: 650px;
  
  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const BoldBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid var(--primary);
`;

const Heading = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  span {
    color: var(--primary);
    position: relative;
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  line-height: 1.6;
  font-weight: 300;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary);
  color: white;
  font-weight: 700;
  padding: 1.25rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: white;
    color: var(--primary);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const DiscountTag = styled.div`
  position: absolute;
  top: -15px;
  right: -15px;
  background-color: #FFAA8A;
  color: #2D3142;
  font-weight: 700;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  transform: rotate(5deg);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  font-size: 1rem;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
`;

const TrustLogos = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 2rem;
  
  img {
    height: 45px;
    filter: brightness(0) invert(1);
    transition: filter 0.3s ease;
    
    &:hover {
      filter: brightness(0) invert(1) drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
    }
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    
    img {
      height: 40px;
    }
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: perspective(1000px) rotateY(-5deg);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: perspective(1000px) rotateY(0);
  }
`;

const PowerSection = styled.section`
  padding: 6rem 0;
  background-color: white;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #2D3142;
  text-align: center;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background-color: var(--primary);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #6C757D;
  margin: 2rem auto 3rem;
  text-align: center;
  max-width: 800px;
`;

const PowerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PowerCard = styled.div`
  background-color: ${props => props.bgColor || '#f8f9fa'};
  border-radius: 8px;
  padding: 3rem;
  text-align: left;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  height: 100%;
  
  h3 {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: ${props => props.titleColor || '#2D3142'};
  }
`;

const PowerList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    position: relative;
    padding-left: 2rem;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    color: ${props => props.textColor || '#6C757D'};
    
    &:before {
      content: '✓';
      position: absolute;
      left: 0;
      color: ${props => props.checkColor || 'var(--primary)'};
      font-weight: bold;
    }
  }
`;

const PartnersSection = styled.section`
  padding: 6rem 0;
  background-color: #f8f9fa;
`;

const PartnerCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PartnerLogo = styled.div`
  width: 120px;
  height: 120px;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 2rem;
  
  img {
    max-width: 100%;
    max-height: 100%;
  }
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 1.5rem;
  }
`;

const PartnerContent = styled.div``;

const PartnerTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #2D3142;
`;

const PartnerDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #E94057 0%, #F27121 100%);
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  span {
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 8px;
      background-color: rgba(255, 255, 255, 0.3);
      z-index: -1;
    }
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: white;
  color: var(--primary);
  font-weight: 700;
  padding: 1.25rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: #2D3142;
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const LandingPageBold = () => {
  // Google Analytics tracking code setup
  useEffect(() => {
    // Track page view for this landing page
    ReactGA.send({
      hitType: "pageview",
      page: "/landing-bold",
      title: "Bold Landing Page"
    });

    // Event tracking for CTA clicks
    const trackCTAClick = () => {
      ReactGA.event({
        category: 'engagement',
        action: 'cta_click',
        label: 'Bold Landing Page CTA',
        value: 1
      });
    };
    // Add event listeners to CTA buttons
    const ctaButtons = document.querySelectorAll('.cta-button');
    ctaButtons.forEach(button => {
      button.addEventListener('click', trackCTAClick);
    });

    return () => {
      // Clean up event listeners
      ctaButtons.forEach(button => {
        button.removeEventListener('click', trackCTAClick);
      });
    };
  }, []);

  return (
    <LandingContainer>
      <HeroSection>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <HeroContent>
                <BoldBadge>
                  <i className="bi bi-stars me-2"></i>
                  SLEEP SOLUTIONS FOR AUSTRALIAN FAMILIES
                </BoldBadge>
                <Heading>
                  ARTIFICIAL INTELLIGENCE (AI)-POWERED SLEEP PLANS INFORMED BY <span>AUSTRALIA'S TRUSTED RESOURCES</span>
                </Heading>
                <SubHeading>
                  We combine cutting-edge Artificial Intelligence (AI) with evidence-based guidelines from respected resources like Red Nose Australia, Raising Children Network, and other Australian sleep authorities to create personalized sleep plans designed to help improve your baby's sleep.
                </SubHeading>
                <ButtonWrapper>
                  <CtaButton to="/signup" className="cta-button">
                    GET YOUR SLEEP PLAN NOW
                  </CtaButton>
                </ButtonWrapper>
              </HeroContent>
            </div>
            <div className="col-lg-5">
              <HeroImageContainer>
                <HeroImage 
                  src="/images/jonathan-borba-RWgE9_lKj_Y-unsplash.jpg" 
                  alt="Peaceful sleeping baby" 
                />
              </HeroImageContainer>
            </div>
          </div>
        </div>
      </HeroSection>

      <PowerSection>
        <div className="container">
          <SectionTitle>THE POWER OF AI + EVIDENCE-BASED GUIDELINES</SectionTitle>
          <SectionSubtitle>
            We've created an innovative approach to baby sleep by combining Artificial Intelligence technology with research from respected Australian sleep resources.
          </SectionSubtitle>
          
          <PowerGrid>
            <PowerCard bgColor="rgba(233, 64, 87, 0.05)" titleColor="var(--primary)">
              <h3>CUTTING-EDGE AI TECHNOLOGY</h3>
              <PowerList checkColor="var(--primary)">
                <li>Analyzes key data points about your child's unique sleep patterns</li>
                <li>Creates a personalized sleep plan tailored specifically to your baby</li>
                <li>Adapts recommendations based on your parenting style preferences</li>
                <li>Provides plans without waiting for consultations</li>
                <li>Continuously improves based on feedback</li>
              </PowerList>
            </PowerCard>
            
            <PowerCard bgColor="rgba(126, 203, 185, 0.05)" titleColor="var(--secondary)">
              <h3>EVIDENCE-BASED EXPERTISE</h3>
              <PowerList checkColor="var(--secondary)">
                <li>Red Nose Australia safe sleep guidelines to reduce SIDS risk</li>
                <li>Evidence-based approaches for gentle sleep solutions</li>
                <li>Raising Children Network developmental research for age-appropriate plans</li>
                <li>Australian sleep resources for healthy sleep habits</li>
              </PowerList>
            </PowerCard>
          </PowerGrid>
        </div>
      </PowerSection>

      <PartnersSection>
        <div className="container">
          <SectionTitle>RESOURCES WE REFERENCE</SectionTitle>
          <SectionSubtitle>
            We reference Australia's respected sleep and parenting resources to ensure our recommendations are safe and evidence-based.
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-md-6">
              <PartnerCard>
                <PartnerLogo>
                  <img src="/images/logo-round-rednose.svg" alt="Red Nose Australia" />
                </PartnerLogo>
                <PartnerContent>
                  <PartnerTitle>Red Nose Australia</PartnerTitle>
                  <PartnerDescription>
                    Australia's leading authority on safe sleep practices and SIDS prevention. Our sleep plans reference Red Nose guidelines to help prioritize your baby's safety.
                  </PartnerDescription>
                </PartnerContent>
              </PartnerCard>
            </div>
            
            <div className="col-md-6">
              <PartnerCard>
                <PartnerLogo>
                  <img src="/images/logo-triple-p.png" alt="Triple P" />
                </PartnerLogo>
                <PartnerContent>
                  <PartnerTitle>Triple P</PartnerTitle>
                  <PartnerDescription>
                    The Positive Parenting Program provides evidence-based strategies. We reference their approach to support positive sleep habits and family wellbeing.
                  </PartnerDescription>
                </PartnerContent>
              </PartnerCard>
            </div>
            
            <div className="col-md-6">
              <PartnerCard>
                <PartnerLogo>
                  <img src="/images/logo-raisingchildren.png" alt="Raising Children Network" />
                </PartnerLogo>
                <PartnerContent>
                  <PartnerTitle>Raising Children Network</PartnerTitle>
                  <PartnerDescription>
                    Australian parenting website backed by the Australian Government. We reference their research to ensure our sleep plans are developmentally appropriate.
                  </PartnerDescription>
                </PartnerContent>
              </PartnerCard>
            </div>
            
            <div className="col-md-6">
              <PartnerCard>
                <PartnerLogo>
                  <img src="/images/logo-tresilian.png" alt="Tresillian" />
                </PartnerLogo>
                <PartnerContent>
                  <PartnerTitle>Tresillian</PartnerTitle>
                  <PartnerDescription>
                    Australia's largest early parenting support organization. We reference their expertise in early childhood development and sleep guidance.
                  </PartnerDescription>
                </PartnerContent>
              </PartnerCard>
            </div>
          </div>
        </div>
      </PartnersSection>

      <CTASection>
        <div className="container">
          <CTATitle>
            GET THE <span>BEST OF BOTH WORLDS</span>
          </CTATitle>
          <CTADescription>
            Advanced Artificial Intelligence technology combined with evidence-based guidelines. Create your personalized sleep plan today and join Australian families seeking better sleep.
          </CTADescription>
          <CTAButtonWrapper>
            <CTAButton to="/signup" className="cta-button">
              START NOW
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </CTASection>
    </LandingContainer>
  );
};

export default LandingPageBold;
